import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface AllEvent {
  id: string;
  name: string;
}

export interface EventInfoData {
  id: string;
  name: string;
  mealInfo: string | null;
  parkingInfo: string | null;
  parkingImageUrl: string | null;
  transportInfo: string | null;
}

interface RsvpCardProps {
  /** User's registered event IDs with their RSVP status (from my-weekend API) */
  userRsvps: { id: string; rsvpStatus?: string | null }[];
  /** Enriched event info map keyed by event ID (from my-weekend portal response) */
  eventInfoMap?: Record<string, EventInfoData>;
}

export function RsvpCard({ userRsvps, eventInfoMap }: RsvpCardProps) {
  const [allEvents, setAllEvents] = useState<AllEvent[]>([]);
  const [rsvps, setRsvps] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  // Click-outside dismissal
  useEffect(() => {
    if (!openTooltip) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-tooltip-container]')) {
        setOpenTooltip(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openTooltip]);

  // Escape key dismissal
  useEffect(() => {
    if (!openTooltip) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenTooltip(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openTooltip]);

  useEffect(() => {
    // Fetch ALL events globally and user's RSVPs
    Promise.all([
      apiClient<AllEvent[]>('/events'),
      apiClient<{ eventId: string; status: string }[]>('/my-weekend/rsvps').catch(() => []),
    ])
      .then(([events, userRsvpRecords]) => {
        setAllEvents(events);
        // Initialize RSVP map from user's actual RSVP records
        const map: Record<string, string | null> = {};
        events.forEach(e => { map[e.id] = null; });
        // First apply from my-weekend data (props)
        userRsvps.forEach(r => { if (r.id in map) map[r.id] = r.rsvpStatus || null; });
        // Then override with direct RSVP records (more authoritative)
        userRsvpRecords.forEach(r => { if (r.eventId in map) map[r.eventId] = r.status; });
        setRsvps(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleRsvp = async (eventId: string) => {
    const current = rsvps[eventId];
    const newStatus = current === 'attending' ? 'not_attending' : 'attending';
    setRsvps(prev => ({ ...prev, [eventId]: newStatus }));
    try {
      await apiClient(`/events/${eventId}/event-rsvp`, {
        method: 'POST',
        body: { status: newStatus },
      });
    } catch {
      setRsvps(prev => ({ ...prev, [eventId]: current }));
    }
  };

  const handleIconClick = (e: React.MouseEvent, tooltipKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenTooltip(prev => prev === tooltipKey ? null : tooltipKey);
  };

  if (loading) return null;
  if (allEvents.length === 0) return null;

  const goingCount = Object.values(rsvps).filter(s => s === 'attending').length;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.title}>✋ My RSVPs</span>
        <span style={styles.count}>{goingCount}/{allEvents.length}</span>
      </div>
      <div style={styles.list}>
        {allEvents.map((event) => {
          const isGoing = rsvps[event.id] === 'attending';
          const info = eventInfoMap?.[event.id];
          return (
            <label key={event.id} style={styles.row}>
              <input
                type="checkbox"
                checked={isGoing}
                onChange={() => toggleRsvp(event.id)}
                style={styles.checkbox}
              />
              <span style={{ ...styles.eventName, color: isGoing ? '#0f2d1f' : '#888' }}>
                {event.name}
              </span>
              {info && (info.mealInfo || info.parkingInfo || info.parkingImageUrl || info.transportInfo) && (
                <span style={styles.iconGroup}>
                  {info.mealInfo !== null && (
                    <span data-tooltip-container style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        type="button"
                        style={iconBtnStyle}
                        aria-label={`Meal info for ${event.name}`}
                        aria-describedby={openTooltip === `${event.id}-meal` ? `tooltip-${event.id}-meal` : undefined}
                        onClick={(e) => handleIconClick(e, `${event.id}-meal`)}
                      >
                        🍽️
                      </button>
                      {openTooltip === `${event.id}-meal` && (
                        <div id={`tooltip-${event.id}-meal`} role="tooltip" style={tooltipStyles}>
                          {info.mealInfo}
                        </div>
                      )}
                    </span>
                  )}
                  {(info.parkingInfo !== null || info.parkingImageUrl) && (
                    <span data-tooltip-container style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        type="button"
                        style={iconBtnStyle}
                        aria-label={`Parking info for ${event.name}`}
                        aria-describedby={openTooltip === `${event.id}-parking` ? `tooltip-${event.id}-parking` : undefined}
                        onClick={(e) => handleIconClick(e, `${event.id}-parking`)}
                      >
                        🅿️
                      </button>
                      {openTooltip === `${event.id}-parking` && (
                        <div id={`tooltip-${event.id}-parking`} role="tooltip" style={tooltipStyles}>
                          {info.parkingInfo && <div style={{ marginBottom: info.parkingImageUrl ? '8px' : 0 }}>{info.parkingInfo}</div>}
                          {info.parkingImageUrl && (
                            <img src={info.parkingImageUrl} alt="Parking map" style={{ maxWidth: '280px', maxHeight: '200px', borderRadius: '4px', display: 'block' }} />
                          )}
                        </div>
                      )}
                    </span>
                  )}
                  {info.transportInfo !== null && (
                    <span data-tooltip-container style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        type="button"
                        style={iconBtnStyle}
                        aria-label={`Transport info for ${event.name}`}
                        aria-describedby={openTooltip === `${event.id}-transport` ? `tooltip-${event.id}-transport` : undefined}
                        onClick={(e) => handleIconClick(e, `${event.id}-transport`)}
                      >
                        🚗
                      </button>
                      {openTooltip === `${event.id}-transport` && (
                        <div id={`tooltip-${event.id}-transport`} role="tooltip" style={tooltipStyles}>
                          {info.transportInfo}
                        </div>
                      )}
                    </span>
                  )}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
  fontSize: '0.85rem',
  borderRadius: '4px',
  lineHeight: 1,
  position: 'relative',
};

const tooltipStyles: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  marginTop: '4px',
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '0.75rem',
  color: '#333',
  maxWidth: '250px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  zIndex: 10,
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    padding: '1rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.6rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.4rem',
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
  },
  count: {
    fontSize: '0.7rem',
    color: '#1a6b3c',
    fontWeight: 600,
    backgroundColor: '#e8f5e9',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.25rem 0',
    cursor: 'pointer',
  },
  checkbox: {
    width: '14px',
    height: '14px',
    accentColor: '#1a6b3c',
    cursor: 'pointer',
    flexShrink: 0,
    margin: 0,
  },
  eventName: {
    fontSize: '0.8rem',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  iconGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    marginLeft: '4px',
  },
};
